import type {
  FieldPresentation,
  FieldSpec,
  SelectChoice,
  SettingsCardSpec,
  SettingsGroupView,
  SettingsValues,
} from './types';

const int = (min?: number, max?: number, step = 1): FieldSpec => ({
  kind: 'number',
  integer: true,
  min,
  max,
  step,
});

const float = (min?: number, max?: number, step = 0.05): FieldSpec => ({
  kind: 'number',
  min,
  max,
  step,
});

const voice = (specs: Record<string, FieldSpec>) =>
  Object.fromEntries(
    Object.entries(specs).map(([leaf, spec]) => [
      `channels.voice.${leaf}`,
      spec,
    ])
  );

// Only leaves whose control is not plain text are listed. Types follow how
// maestro-platform reads each leaf (an Ecto :integer, a float, a map), and the
// bounds mirror its validators, which remain the authority.
const SPECS: Record<string, FieldSpec> = {
  'model_config.temperature': float(0, 2, 0.1),
  'model_config.max_tokens': int(1),
  'decision_config.provider': {
    kind: 'select',
    options: ['typesafe', 'openrouter', 'ollaya'],
  },
  'decision_config.timeout_ms': int(100, undefined, 100),
  ...voice({
    'stt.provider': { kind: 'select', options: ['deepgram', 'google'] },
    'stt.endpointing_ms': int(0, undefined, 10),
    'stt.utterance_end_ms': int(0, undefined, 10),
    'stt.eot_threshold': float(0, 1),
    'stt.eager_eot_threshold': float(0, 1),
    'stt.eot_timeout_ms': int(0, undefined, 100),
    'stt.keyterms': { kind: 'list' },
    'tts.provider': { kind: 'select', options: ['elevenlabs'] },
    'tts.stability': float(0, 1),
    'tts.similarity_boost': float(0, 1),
    'tts.style': float(0, 1),
    'tts.speed': float(0.7, 1.2),
    'tts.use_speaker_boost': { kind: 'toggle' },
    'tts.buffer_flush_ms': int(0, undefined, 10),
    'silence.max_consecutive': int(0, 5),
    'twilio.recording_enabled': { kind: 'toggle' },
  }),
  'privacy.recording_days': int(1),
  'privacy.transcript_days': int(1),
  'privacy.masking_rule_set_id': int(1),
  'live_agent.connection_id': int(1),
};

export const fieldSpec = (leaf: string): FieldSpec =>
  SPECS[leaf] ?? { kind: 'text' };

/** `channels.voice.stt.eot_threshold` -> `Eot threshold`, for leaves an app has not labelled. */
export const humanizeLeaf = (leaf: string): string => {
  const last = leaf.split('.').pop() ?? leaf;
  const words = last.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The translation key segment for a leaf: i18next reads dots as nesting. */
export const leafKey = (leaf: string): string => leaf.replace(/\./g, '_');

/** A value a form field holds that cannot be saved as its leaf's type. */
export const INVALID: unique symbol = Symbol('invalid');

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A leaf's control after a card's presentation is applied: what renders, validates and saves it. */
export type EffectiveSpec =
  | {
      kind: 'select';
      options: SelectChoice[];
      otherLabel?: (value: string) => string;
      numeric?: boolean;
    }
  | {
      kind: 'slider';
      min: number;
      max: number;
      step: number;
      off?: number;
      integer?: boolean;
    }
  | Exclude<FieldSpec, { kind: 'select' }>;

// The leaf's own type stays the authority on what is stored: a select or slider
// over an integer leaf must still save a number.
export const effectiveSpec = (
  leaf: string,
  presentation?: FieldPresentation
): EffectiveSpec => {
  const base = fieldSpec(leaf);
  const control = presentation?.control;
  const numeric = base.kind === 'number';
  if (!control) {
    return base.kind === 'select'
      ? {
          kind: 'select',
          options: base.options.map((value) => ({ value, label: value })),
        }
      : base;
  }
  if (control.kind === 'select')
    return numeric ? { ...control, numeric } : control;
  if (control.kind === 'slider')
    return numeric && base.integer ? { ...control, integer: true } : control;
  if (control.kind === 'number' && numeric) return { ...base, ...control };
  return control;
};

const isOutOfBounds = (n: number, min?: number, max?: number) =>
  (min !== undefined && n < min) || (max !== undefined && n > max);

/**
 * The value a form field holds, back to what the API stores. Blank clears the
 * leaf; text that is not the leaf's type, or outside its bounds, is INVALID
 * rather than dropped, so a typo never silently clears a setting.
 */
export const coerce = (
  spec: FieldSpec | EffectiveSpec,
  raw: unknown
): unknown => {
  if (raw === '' || raw === null || raw === undefined) return undefined;
  switch (spec.kind) {
    case 'number':
    case 'slider': {
      const n = Number(raw);
      if (!Number.isFinite(n)) return INVALID;
      if (spec.kind === 'slider' && n === spec.off) return n;
      if (spec.integer && !Number.isInteger(n)) return INVALID;
      return isOutOfBounds(n, spec.min, spec.max) ? INVALID : n;
    }
    case 'select': {
      if (!('numeric' in spec && spec.numeric)) return raw;
      const n = Number(raw);
      return Number.isFinite(n) ? n : INVALID;
    }
    case 'toggle':
      return Boolean(raw);
    case 'list':
      return Array.isArray(raw)
        ? raw
        : String(raw)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    case 'json': {
      if (typeof raw !== 'string') return isPlainObject(raw) ? raw : INVALID;
      try {
        const parsed: unknown = JSON.parse(raw);
        return isPlainObject(parsed) ? parsed : INVALID;
      } catch {
        return INVALID;
      }
    }
    default:
      return raw;
  }
};

/** What a field shows for a stored value; a map is JSON, never `[object Object]`. */
export const displayValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/** A map-valued leaf with no JSON spec is shown, not edited. */
export const isReadOnlyValue = (
  spec: FieldSpec | EffectiveSpec,
  value: unknown
): boolean => spec.kind !== 'json' && isPlainObject(value);

/**
 * What a scope's form starts from. Overriding takes the whole group, so a first
 * override begins as a copy of what was inherited, not as an empty form.
 */
export const startingValues = (view: SettingsGroupView): SettingsValues =>
  view.values ?? view.inherited.values ?? {};

/**
 * The fields a card renders, in order. A spec field naming a leaf the group
 * does not own is skipped, so a stale spec can never write an unknown leaf.
 */
export const renderedFields = (
  leaves: string[],
  spec?: SettingsCardSpec
): FieldPresentation[] =>
  spec
    ? spec.fields.filter((field) => leaves.includes(field.leaf))
    : leaves.map((leaf) => ({ leaf }));

/** A required field left blank. */
export const isMissing = (field: FieldPresentation, draft: SettingsValues) =>
  Boolean(field.required?.when(draft)) &&
  displayValue(draft[field.leaf]) === '';

/** Only rendered leaves can block a save: nobody can fix a field they cannot see. */
export const invalidLeaves = (
  leaves: string[],
  draft: SettingsValues,
  spec?: SettingsCardSpec
): string[] =>
  renderedFields(leaves, spec)
    .filter(
      (field) =>
        coerce(effectiveSpec(field.leaf, field), draft[field.leaf]) ===
          INVALID || isMissing(field, draft)
    )
    .map((field) => field.leaf);

/**
 * What a save sends. Rendered leaves drop when cleared (or unparseable); a
 * hidden leaf goes back exactly as it came, since the user never touched it.
 */
export const toSavedValues = (
  leaves: string[],
  draft: SettingsValues,
  spec?: SettingsCardSpec
): SettingsValues => {
  const shown = new Map(
    renderedFields(leaves, spec).map((field) => [field.leaf, field])
  );
  return Object.fromEntries(
    leaves
      .map((leaf) => {
        const field = shown.get(leaf);
        const value = field
          ? coerce(effectiveSpec(leaf, field), draft[leaf])
          : (draft[leaf] ?? undefined);
        return [leaf, value] as const;
      })
      .filter(([, value]) => value !== undefined && value !== INVALID)
  );
};

/** The value of the DS input or select an event came from. */
export const valueOf = (event: { target: unknown }): string =>
  (event.target as { value: string }).value;

export const checkedOf = (event: { target: unknown }): boolean =>
  (event.target as { checked: boolean }).checked;
